import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Dialog as D } from "radix-ui";
import type { Media } from "@atrium/shared";
import { cn } from "../lib/cn";
import { decryptedUrl, placeholderUrl } from "../lib/e2ee/files";
import { usePrefs } from "../lib/prefs";
import { ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator, ContextMenuTrigger } from "./ui/ContextMenu";
import { toast } from "./ui/Toast";
import { IconArrowRightUp, IconChevronLeft, IconChevronRight, IconImage, IconLink, IconLock, IconRefresh, IconUser, IconX } from "./icons";

/** Absolute URL for an image, so copied links work outside the app. */
const absoluteUrl = (url: string) => new URL(url, window.location.href).href;

/**
 * Puts the image itself on the clipboard. Browsers only accept PNG, so other
 * formats are redrawn onto a canvas first. The ClipboardItem gets a promise
 * so Safari still treats the write as part of the click.
 */
async function copyImage(url: string) {
  const png = (async () => {
    const blob = await (await fetch(url)).blob();
    if (blob.type === "image/png") return blob;
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")!.drawImage(bitmap, 0, 0);
    bitmap.close();
    return new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode"))), "image/png"));
  })();
  await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
}

function saveImage(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
}

/**
 * Right-click on any image: copy it, copy its link, or open it in a new tab.
 * Decrypted images (object URLs) have no link worth sharing, so they offer saving instead.
 */
export function ImageContextMenu({ url, children, extra, filename }: { url: string; children: ReactNode; extra?: ReactNode; filename?: string | null }) {
  const local = url.startsWith("blob:");
  const run = async (work: () => Promise<void>, done: string) => {
    try {
      await work();
      toast.show(done);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem icon={IconImage} onSelect={() => void run(() => copyImage(url), "Image copied")}>
          Copy image
        </ContextMenuItem>
        {local ? (
          <ContextMenuItem icon={IconArrowRightUp} onSelect={() => saveImage(url, filename || "image")}>
            Save image
          </ContextMenuItem>
        ) : (
          <>
            <ContextMenuItem icon={IconLink} onSelect={() => void run(() => navigator.clipboard.writeText(absoluteUrl(url)), "Link copied")}>
              Copy image link
            </ContextMenuItem>
            <ContextMenuItem icon={IconArrowRightUp} onSelect={() => window.open(absoluteUrl(url), "_blank", "noopener,noreferrer")}>
              Open image link
            </ContextMenuItem>
          </>
        )}
        {extra}
      </ContextMenuContent>
    </ContextMenu>
  );
}

const ZOOM_KEY = "atrium:image-zoom";
type ZoomSettings = {
  zoom: number;
  size: number;
  square: boolean;
  pixelated: boolean;
};
const ZOOM_DEFAULTS: ZoomSettings = {
  zoom: 2,
  size: 160,
  square: false,
  pixelated: false,
};

function loadZoom(): ZoomSettings {
  try {
    return {
      ...ZOOM_DEFAULTS,
      ...JSON.parse(localStorage.getItem(ZOOM_KEY) ?? "{}"),
    };
  } catch {
    return ZOOM_DEFAULTS;
  }
}

/**
 * Image in the viewer with a magnifying lens: hold the mouse button down to
 * show it, move to pan, scroll to change the zoom and Shift+scroll to change
 * the lens size. The lens shape and pixel scaling are in the right-click menu,
 * and all of it is remembered between images and visits.
 */
function ZoomableImage({ src }: { src: string }) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [settings, setSettings] = useState(loadZoom);
  const [lens, setLens] = useState<{ x: number; y: number } | null>(null);
  const update = useCallback((patch: Partial<ZoomSettings>) => {
    setSettings((s) => {
      const next = { ...s, ...patch };
      localStorage.setItem(ZOOM_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const active = lens !== null;
  useEffect(() => {
    if (!active) return;
    const move = (e: PointerEvent) => setLens({ x: e.clientX, y: e.clientY });
    const up = () => {
      setLens(null);
      // Letting go off the image would land a click on the backdrop and close the viewer.
      const swallow = (e: MouseEvent) => e.stopPropagation();
      window.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);
    };
    // Wheel must be non-passive to stop the page (and the dialog) scrolling.
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      setSettings((s) => {
        const next = e.shiftKey
          ? { ...s, size: Math.min(600, Math.max(60, s.size + dir * 20)) }
          : {
              ...s,
              zoom: Math.min(10, Math.max(1.25, Math.round((s.zoom + dir * 0.25 * Math.max(1, s.zoom / 2)) * 100) / 100)),
            };
        localStorage.setItem(ZOOM_KEY, JSON.stringify(next));
        return next;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("blur", up);
    window.addEventListener("wheel", wheel, { passive: false });
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("blur", up);
      window.removeEventListener("wheel", wheel);
    };
  }, [active]);

  const rect = active ? imgRef.current?.getBoundingClientRect() : undefined;
  const { zoom, size, square, pixelated } = settings;
  const lensEl =
    lens && rect
      ? createPortal(
          <div
            aria-hidden
            className={cn(
              "pointer-events-none fixed z-[75] border-2 border-white/80 bg-black bg-no-repeat shadow-[0_8px_30px_rgba(0,0,0,0.6)]",
              square ? "rounded-lg" : "rounded-full",
            )}
            style={{
              width: size,
              height: size,
              left: lens.x - size / 2,
              top: lens.y - size / 2,
              backgroundImage: `url("${src}")`,
              backgroundSize: `${rect.width * zoom}px ${rect.height * zoom}px`,
              backgroundPosition: `${size / 2 - (lens.x - rect.left) * zoom}px ${size / 2 - (lens.y - rect.top) * zoom}px`,
              imageRendering: pixelated ? "pixelated" : "auto",
            }}
          />,
          document.body,
        )
      : null;

  return (
    <>
      <ImageContextMenu
        url={src}
        extra={
          <>
            <ContextMenuSeparator />
            <ContextMenuLabel>
              Zoom {zoom}× · lens {size}px
            </ContextMenuLabel>
            <ContextMenuCheckboxItem checked={square} onCheckedChange={(v) => update({ square: v })}>
              Square lens
            </ContextMenuCheckboxItem>
            <ContextMenuCheckboxItem checked={pixelated} onCheckedChange={(v) => update({ pixelated: v })}>
              Sharp pixels
            </ContextMenuCheckboxItem>
            <ContextMenuItem icon={IconRefresh} onSelect={() => update({ zoom: ZOOM_DEFAULTS.zoom, size: ZOOM_DEFAULTS.size })}>
              Reset zoom
            </ContextMenuItem>
          </>
        }
      >
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          className={cn("max-h-[92dvh] max-w-[94vw] select-none object-contain animate-pop-in", active ? "cursor-none" : "cursor-zoom-in")}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => {
            if (e.button !== 0 || e.pointerType === "touch") return;
            e.preventDefault();
            setLens({ x: e.clientX, y: e.clientY });
          }}
        />
      </ImageContextMenu>
      {lensEl}
    </>
  );
}

/**
 * Feed video. With autoplay on (and motion not reduced) it plays muted while
 * mostly on screen and pauses when scrolled away; otherwise it waits for a tap.
 */
function FeedVideo({ m, cover, className, autoplay }: { m: Media; cover: boolean; className?: string; autoplay: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !autoplay || document.documentElement.dataset.motion === "reduce") return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) void el.play().catch(() => {});
        else if (!el.paused) el.pause();
      },
      { threshold: [0, 0.6] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [autoplay]);
  return (
    <video
      ref={ref}
      src={m.url}
      controls
      preload="metadata"
      playsInline
      muted={autoplay}
      loop={autoplay}
      className={cn("h-full w-full bg-black", cover ? "object-cover" : "object-contain", className)}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

/**
 * People tagged on an image: a small button in the corner reveals name labels
 * at the tagged positions. Renders nothing for untagged media.
 */
export function ImageTagLayer({ m }: { m: Media }) {
  const [shown, setShown] = useState(false);
  const tags = m.tags ?? [];
  if (m.kind !== "image" || tags.length === 0) return null;
  return (
    <>
      {shown &&
        tags.map((t) => (
          <a
            key={t.user.id}
            href={`/u/${t.user.username}`}
            onClick={(e) => e.stopPropagation()}
            className="absolute z-[1] -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-black/75 px-1.5 py-0.5 text-xs font-medium text-white animate-fade-in hover:bg-black/90"
            style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }}
          >
            {t.user.displayName}
          </a>
        ))}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setShown((v) => !v);
        }}
        aria-pressed={shown}
        aria-label={shown ? "Hide tagged people" : `Show ${tags.length} tagged ${tags.length === 1 ? "person" : "people"}`}
        className="absolute bottom-1.5 left-1.5 z-[1] flex size-7 items-center justify-center rounded-full bg-black/65 text-white hover:bg-black/80"
      >
        <IconUser className="icon size-4" aria-hidden />
      </button>
    </>
  );
}

function MediaItem({ m, className, onOpen, cover = true, autoplay = false }: { m: Media; className?: string; onOpen?: () => void; cover?: boolean; autoplay?: boolean }) {
  if (m.kind === "video") return <FeedVideo m={m} cover={cover} className={className} autoplay={autoplay} />;
  return (
    <ImageContextMenu url={m.url} filename={m.filename}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.();
        }}
        className={cn("block h-full w-full cursor-zoom-in", className)}
        aria-label="Open image"
      >
        <img src={m.url} alt="" loading="lazy" decoding="async" className={cn("h-full w-full bg-surface-2", cover ? "object-cover" : "object-contain")} />
      </button>
    </ImageContextMenu>
  );
}

/**
 * Feed media: single, grid (2–4) or swipeable carousel (5+). Honours the
 * autoplay and density preferences; compact density uses shorter media.
 */
export function MediaGrid({ media, className }: { media: Media[]; className?: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const prefs = usePrefs();
  const autoplay = prefs.autoplayMedia;
  const dense = prefs.density === "compact";
  if (media.length === 0) return null;
  const n = media.length;

  let body;
  if (n === 1) {
    const m = media[0];
    body =
      m.kind === "image" ? (
        <div className="relative w-fit max-w-full overflow-hidden rounded-lg border border-line">
          <ImageTagLayer m={m} />
          <ImageContextMenu url={m.url}>
            <button type="button" onClick={(e) => (e.stopPropagation(), setOpen(0))} className="block cursor-zoom-in" aria-label="Open image">
              <img
                src={m.url}
                alt=""
                loading="lazy"
                decoding="async"
                width={m.width ?? undefined}
                height={m.height ?? undefined}
                className={cn("block h-auto w-auto max-w-full bg-surface-2 object-contain", dense ? "max-h-[380px]" : "max-h-[540px]")}
              />
            </button>
          </ImageContextMenu>
        </div>
      ) : (
        <div className={cn("aspect-video overflow-hidden rounded-lg border border-line bg-black", dense ? "max-h-[380px] max-w-[680px]" : "max-h-[540px]")}>
          <MediaItem m={m} cover={false} autoplay={autoplay} />
        </div>
      );
  } else if (n <= 4) {
    body = (
      <div className={cn("grid w-full gap-0.5 overflow-hidden rounded-lg border border-line", "grid-cols-2", n > 2 && "grid-rows-2", dense ? "aspect-[2/1] max-h-[360px]" : "aspect-[16/10] max-h-[500px]")}>
        {media.map((m, i) => (
          <div key={m.id} className={cn("relative min-h-0 overflow-hidden", n === 3 && i === 0 && "row-span-2")}>
            <ImageTagLayer m={m} />
            <MediaItem m={m} onOpen={() => setOpen(i)} autoplay={autoplay} />
          </div>
        ))}
      </div>
    );
  } else {
    body = <Carousel media={media} onOpen={setOpen} autoplay={autoplay} dense={dense} />;
  }

  return (
    <div className={className}>
      {body}
      <Lightbox media={media.filter((m) => m.kind === "image")} index={open === null ? null : media.filter((m) => m.kind === "image").indexOf(media[open])} onClose={() => setOpen(null)} />
    </div>
  );
}

function Carousel({ media, onOpen, autoplay, dense }: { media: Media[]; onOpen: (i: number) => void; autoplay: boolean; dense: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const scrollTo = (i: number) => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };
  return (
    <div className="group relative overflow-hidden rounded-lg border border-line" data-swipe-ignore>
      <div
        ref={ref}
        className={cn("no-scrollbar flex aspect-square w-full snap-x snap-mandatory overflow-x-auto sm:aspect-[4/3]", dense ? "max-h-[400px]" : "max-h-[580px]")}
        onScroll={(e) => setIndex(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}
      >
        {media.map((m, i) => (
          <div key={m.id} className="relative h-full w-full shrink-0 snap-center">
            <ImageTagLayer m={m} />
            <MediaItem m={m} onOpen={() => onOpen(i)} autoplay={autoplay} />
          </div>
        ))}
      </div>
      <span className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white tabular">
        {index + 1}/{media.length}
      </span>
      {index > 0 && (
        <button
          aria-label="Previous"
          onClick={(e) => {
            e.stopPropagation();
            scrollTo(index - 1);
          }}
          className="absolute left-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
        >
          <IconChevronLeft className="icon size-[18px]" />
        </button>
      )}
      {index < media.length - 1 && (
        <button
          aria-label="Next"
          onClick={(e) => {
            e.stopPropagation();
            scrollTo(index + 1);
          }}
          className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
        >
          <IconChevronRight className="icon size-[18px]" />
        </button>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center gap-1">
        {media.map((m, i) => (
          <span key={m.id} className={cn("size-1.5 rounded-full transition-colors", i === index ? "bg-white" : "bg-white/40")} />
        ))}
      </div>
    </div>
  );
}

export function Lightbox({ media, index, onClose }: { media: Media[]; index: number | null; onClose: () => void }) {
  const [i, setI] = useState(index ?? 0);
  useEffect(() => {
    if (index !== null && index >= 0) setI(index);
  }, [index]);
  const prev = useCallback(() => setI((x) => Math.max(0, x - 1)), []);
  const next = useCallback(() => setI((x) => Math.min(media.length - 1, x + 1)), [media.length]);
  const open = index !== null && index >= 0 && media.length > 0;
  const cur = media[i];
  return (
    <D.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-[70] bg-black/90 animate-fade-in" />
        <D.Content
          className="fixed inset-0 z-[70] flex items-center justify-center focus:outline-none"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
          }}
          onClick={onClose}
        >
          <D.Title className="sr-only">Image viewer</D.Title>
          <D.Description className="sr-only">Use the arrow keys to move between images</D.Description>
          {cur && <ZoomableImage key={cur.url} src={cur.url} />}
          <span className="pointer-events-none absolute left-4 top-5 hidden text-xs text-white/50 [@media(hover:hover)]:block">
            Hold to zoom · scroll to change zoom · Shift+scroll to resize the lens
          </span>
          <D.Close aria-label="Close" className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20">
            <IconX className="icon" />
          </D.Close>
          {media.length > 1 && (
            <>
              <button
                aria-label="Previous image"
                disabled={i === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                className="absolute left-4 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 disabled:opacity-30"
              >
                <IconChevronLeft className="icon" />
              </button>
              <button
                aria-label="Next image"
                disabled={i === media.length - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                className="absolute right-4 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 disabled:opacity-30"
              >
                <IconChevronRight className="icon" />
              </button>
              <span className="absolute bottom-5 rounded-full bg-white/10 px-3 py-1 text-sm text-white tabular">
                {i + 1} / {media.length}
              </span>
            </>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/**
 * End-to-end encrypted images, decrypted on this device. Until one is ready its
 * url is empty (shown as its blurred placeholder); `failed` lists the ones that
 * couldn't be downloaded or didn't match their message.
 */
function useDecryptedMedia(media: Media[]) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<Record<string, true>>({});
  useEffect(() => {
    let live = true;
    for (const m of media) {
      if (!m.e2ee) continue;
      decryptedUrl(m)
        .then((u) => live && setUrls((x) => (x[m.id] === u ? x : { ...x, [m.id]: u })))
        .catch(() => live && setFailed((x) => ({ ...x, [m.id]: true })));
    }
    return () => {
      live = false;
    };
  }, [media]);
  return { resolved: media.map((m) => (m.e2ee ? { ...m, url: urls[m.id] ?? "" } : m)), failed };
}

/** An encrypted image still downloading (its ThumbHash, blurred) or one that couldn't be opened. */
function EncryptedPending({ m, failed }: { m: Media; failed: boolean }) {
  const ph = m.e2ee ? placeholderUrl(m.e2ee) : null;
  return (
    <div className="relative flex h-full w-full items-center justify-center bg-surface-2" role="img" aria-label={failed ? "Encrypted image that couldn't be opened" : "Decrypting image"}>
      {ph && !failed && <img src={ph} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      {failed && (
        <span className="flex items-center gap-1.5 px-3 text-center text-xs text-fg-3">
          <IconLock className="icon shrink-0" /> This image couldn't be decrypted
        </span>
      )}
    </div>
  );
}

/** Compact media for chat messages. */
export function ChatMedia({ media }: { media: Media[] }) {
  const [open, setOpen] = useState<number | null>(null);
  const { resolved, failed } = useDecryptedMedia(media);
  if (!media.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {resolved.map((m, i) => {
        const w = Math.min(m.width ?? 320, 360);
        const h = m.width && m.height ? Math.min((w / m.width) * m.height, 320) : 220;
        return (
          <div key={m.id} className="overflow-hidden rounded-lg border border-line bg-surface" style={{ width: w, maxWidth: "100%", height: h }}>
            {m.url ? <MediaItem m={m} onOpen={() => setOpen(i)} /> : <EncryptedPending m={m} failed={!!failed[m.id]} />}
          </div>
        );
      })}
      <Lightbox media={resolved.filter((m) => m.url)} index={open === null ? null : resolved.filter((m, j) => m.url && j < open).length} onClose={() => setOpen(null)} />
    </div>
  );
}
